import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="modal"
export default class extends Controller {
  static targets = ["dialog"];
  static values = {
    preventDismiss: { type: Boolean, default: false },
    titleContext: String, // Optional: Context for page title (e.g., "New Objective")
  };

  connect() {
    // Store original title for restoration
    this.originalTitle = null;

    // Set up the scrollbar compensation CSS variable
    this.setScrollbarCompensation();

    // Bind the keydown event to the dialog if present
    if (this.hasDialogTarget) {
      this._boundKeydown = this.keydown.bind(this);
      this.dialogTarget.addEventListener("keydown", this._boundKeydown);
    }

    // Allow dynamic content to open/close via a custom event
    this.boundExternalOpen = (e) => {
      const src = e?.detail?.src || "";
      const reset = e?.detail?.reset ?? true;

      // Persist BlockNote context (used by media_browser_submit_controller)
      if (e?.detail?.blockType) {
        window.mediaBrowserBlockType = e.detail.blockType;
        window.mediaBrowserBlockId = e.detail.blockId; // Store block ID for unique identification
      } else {
        // Clear any previous block context when opening without one
        window.mediaBrowserBlockType = null;
        window.mediaBrowserBlockId = null;
      }

      if (src) {
        // allow per-click URLs
        this.turboFrameSrcValue = src;
        this.lazyLoadValue = true;
        // force reload when URL changes or when caller requests reset
        if (reset || this._lastSrc !== src) this.contentLoaded = false;
        this._lastSrc = src;
      }

      this.open();
    };
    this.boundExternalClose = (e) => {
      // optional if you have nested modals:
      e.stopPropagation();
      this.close();
    };

    // listen on the dialog node (or use this.element if you prefer)
    this.element.addEventListener("modal:open", this.boundExternalOpen);
    this.element.addEventListener("modal:close", this.boundExternalClose);

    // Close the modal automatically when a form inside it submits successfully
    this._onSubmitEnd = (e) => {
      try {
        // Only react to forms that live inside this modal's dialog
        const form = e.target;
        if (!this.hasDialogTarget || !form || !this.dialogTarget.contains(form))
          return;

        // Check if form has prevent-modal-close attribute
        if (form.dataset.preventModalClose === "true") {
          return; // Don't auto-close for forms that need to show intermediate results
        }

        // Turbo sets detail.success in recent versions; fall back to fetchResponse.succeeded
        const detail = e.detail || {};
        const succeeded =
          detail.success === true ||
          (detail.fetchResponse && detail.fetchResponse.succeeded === true) ||
          (detail.fetchResponse &&
            detail.fetchResponse.response &&
            detail.fetchResponse.response.ok === true);

        if (succeeded) {
          this.close();
        }
      } catch (_) {
        // no-op: failing to auto-close should not break form behavior
      }
    };
    // Listen on the dialog so events from child forms bubble up
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener("turbo:submit-end", this._onSubmitEnd);
    }
  }

  disconnect() {
    // Restore title if modal was open
    this.restoreTitle();

    if (this.hasDialogTarget && this._boundKeydown) {
      this.dialogTarget.removeEventListener("keydown", this._boundKeydown);
      this._boundKeydown = null;
    }
    this.element.removeEventListener("modal:open", this.boundExternalOpen);
    this.element.removeEventListener("modal:close", this.boundExternalClose);
    if (this.hasDialogTarget && this._onSubmitEnd) {
      this.dialogTarget.removeEventListener(
        "turbo:submit-end",
        this._onSubmitEnd,
      );
      this._onSubmitEnd = null;
    }
  }

  // NOTE: now async so we can await lazy loads
  async open(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();

      // Legacy paths you have — keep if you still use them
      const button = event.currentTarget;
      const modalUrl = button?.dataset?.modalUrl;
      const projectId = button?.dataset?.projectId;

      if (modalUrl) {
        this.dialogElement = document.getElementById(
          "delete-confirmation-modal",
        );
        if (this.dialogElement) {
          this.loadModalContent(modalUrl); // legacy fetch path
          this.dialogElement.showModal();
          document.body.classList.add("modal-open");
          this.updateTitle(); // Update title when modal opens
          return;
        }
      }

      if (projectId) {
        this.dialogElement = document.getElementById("location-modal-dialog");
        if (this.dialogElement) {
          this.loadLocationContent(projectId); // legacy fetch path
          this.dialogElement.showModal();
          document.body.classList.add("modal-open");
          this.updateTitle(); // Update title when modal opens
          return;
        }
      }
    }

    // Default behavior for other modals (+ new lazy load path)
    if (!this.hasDialogTarget) {
      console.warn(
        "[modal] No dialog target found in this scope",
        this.element,
      );
      return;
    }

    // If a URL was provided (via data or modal:open detail), lazy-load the frame before showing
    if (this.lazyLoadValue && this.turboFrameSrcValue && !this.contentLoaded) {
      await this.loadTemplateContent();
      this.contentLoaded = true;
    }

    this.dialogTarget.showModal();
    document.body.classList.add("modal-open");
    this.resetModalForm();
    this.focusFirstElement();
    this.updateTitle(); // Update title when modal opens
  }

  close(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this._closeDialog();
  }

  // Close the modal without preventing default - use for links that need to proceed
  closeAndProceed() {
    this._closeDialog();
  }

  _closeDialog() {
    // Restore title before closing
    this.restoreTitle();

    // Check if we have a location modal element
    const locationModal = document.getElementById("location-modal-dialog");
    const targetDialog =
      this.dialogElement ||
      (this.hasDialogTarget ? this.dialogTarget : null) ||
      locationModal;

    if (targetDialog) {
      targetDialog.setAttribute("closing", "");

      // Wait for animation to complete before closing
      setTimeout(() => {
        targetDialog.close();
        targetDialog.removeAttribute("closing");
        document.body.classList.remove("modal-open");
      }, 200);
    }
  }

  selectProject(event) {
    const projectId = event.currentTarget.dataset.projectId;
    const projectName = event.currentTarget.dataset.projectName;

    if (!projectId) {
      console.error("No project ID found");
      return;
    }

    // Close the project selection modal first
    this.close();

    // Redirect to template creation with project context
    // For now, we'll use the template creator controller approach
    setTimeout(() => {
      const templateCreatorElement = document.createElement("div");
      templateCreatorElement.setAttribute(
        "data-controller",
        "template-creator",
      );
      templateCreatorElement.setAttribute(
        "data-template-creator-project-id-value",
        projectId,
      );
      document.body.appendChild(templateCreatorElement);

      // Trigger template creation
      const event = new CustomEvent("template:create", {
        detail: { projectId },
      });
      templateCreatorElement.dispatchEvent(event);
    }, 200);
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  backdropClose(event) {
    if (this.preventDismissValue) {
      return;
    }

    // Only close if clicking on the dialog itself (backdrop), not on child elements
    if (event.target === this.dialogTarget) {
      this.close();
    }
  }

  // Handle ESC key
  keydown(event) {
    if (event.key === "Escape" && !this.preventDismissValue) {
      this.close();
    }
  }

  focusFirstElement() {
    // Find the first focusable element in the modal
    const focusableElements = this.dialogTarget.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  setScrollbarCompensation() {
    // Calculate scrollbar width for fixed element compensation
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--scrollbar-compensation",
      `${scrollbarWidth}px`,
    );
  }

  resetModalForm() {
    // Reset any forms inside the modal when it opens
    // Skip forms with data-no-reset="true" (used for edit forms that load with values)
    if (this.hasDialogTarget) {
      const forms = this.dialogTarget.querySelectorAll("form");
      forms.forEach((form) => {
        // Skip forms that shouldn't be reset (edit forms with pre-populated values)
        if (form.dataset.noReset === "true") return;

        form.reset();
        // Clear any validation errors or messages
        const errorMessages = form.querySelectorAll(
          ".error-message, .field_with_errors",
        );
        errorMessages.forEach((error) => error.remove());
      });
    }
  }

  // ---------- NEW: Title Management Methods ----------

  /**
   * Update page title with modal context
   * Looks for titleContext value or data-title-context attribute
   */
  updateTitle() {
    // Only update title if we have a context
    const context = this.hasTitleContextValue
      ? this.titleContextValue
      : this.element.dataset.titleContext;

    if (context) {
      // Store original title if not already stored
      if (!this.originalTitle) {
        this.originalTitle = document.title;
      }

      // Update title with context in parentheses
      document.title = `(${context}) ${this.originalTitle}`;
    }
  }

  /**
   * Restore the original page title
   */
  restoreTitle() {
    if (this.originalTitle) {
      document.title = this.originalTitle;
      this.originalTitle = null; // Clear stored title
    }
  }

  // ---------- Turbo Frame-based lazy loader ----------
  async loadTemplateContent() {
    // Prefer an explicit target, else look for [data-modal-content], else the dialog itself
    const container =
      (this.hasContentTarget && this.contentTarget) ||
      this.dialogTarget.querySelector("[data-modal-content]") ||
      this.dialogTarget;

    let turboFrame = container.querySelector("turbo-frame");

    if (!turboFrame) {
      turboFrame = document.createElement("turbo-frame");
      turboFrame.id = `${this.dialogTarget.id || "modal"}-frame`;
      container.innerHTML = "";
      container.appendChild(turboFrame);
    }

    const src = this.turboFrameSrcValue;

    // Force reload if same URL was used (cache-bust)
    const needsBust = turboFrame.src === src;
    turboFrame.src = needsBust ? this._bust(src) : src;

    // Wait for the frame to finish loading (with a timeout fallback)
    await new Promise((resolve) => {
      const onLoad = () => {
        turboFrame.removeEventListener("turbo:frame-load", onLoad);
        resolve();
      };
      turboFrame.addEventListener("turbo:frame-load", onLoad);
      setTimeout(resolve, 5000); // fallback if no event fires
    });

    // Refresh focusables now that content is present
    this.focusFirstElement();
  }

  _bust(url) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}_=${Date.now()}`;
  }

  // ---------- Legacy loaders (you can remove these once everything uses Turbo Frames) ----------
  loadModalContent(modalUrl) {
    const modalContent = this.dialogElement?.querySelector(".modal-content");
    if (!modalContent) {
      console.error("Modal content area not found");
      return;
    }
    modalContent.innerHTML = `
      <div class="p-8 text-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
        <p class="mt-2 text-neutral-600">Loading...</p>
      </div>
    `;
    fetch(modalUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        return r.text();
      })
      .then((html) => (modalContent.innerHTML = html))
      .catch((err) => {
        console.error("Error loading modal content:", err);
        modalContent.innerHTML = `
          <div class="p-8 text-center">
            <p class="text-red-600">Error loading confirmation dialog. Please try again.</p>
            <button type="button" class="mt-4 inline-flex items-center px-4 py-2 border text-sm rounded-md"
                    data-action="click->modal#close">Close</button>
          </div>`;
      });
  }

  loadLocationContent(projectId) {
    const modalContent = document.getElementById("modal-content-area");
    if (!modalContent) {
      console.error("Modal content area not found");
      return;
    }
    fetch(`/projects/${projectId}/new_location_modal`)
      .then((r) => r.text())
      .then((html) => {
        modalContent.innerHTML = html;
        // Avoid manual script execution if you can—prefer Turbo Frames + Stimulus in the partial
        const scripts = modalContent.getElementsByTagName("script");
        for (let script of scripts) {
          const s = document.createElement("script");
          if (script.src) s.src = script.src;
          else s.textContent = script.innerHTML;
          document.head.appendChild(s);
          document.head.removeChild(s);
        }
      })
      .catch(() => {
        modalContent.innerHTML =
          '<div class="p-8 text-center"><p class="text-red-600">Error loading form. Please try again.</p></div>';
      });
  }
}
